import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from '@lingui/macro';
import isEmpty from 'lodash/isEmpty';

import rem from 'polished/lib/helpers/rem';

import spacing from '@decisiv/design-tokens/lib/spacing';

import Search from '@decisiv/iconix/lib/components/Search';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';

import { Item } from '@decisiv/ui-components/lib/atoms/OptionsList/types';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import {
  InstanceRef,
  PopoverProps,
} from '@decisiv/ui-components/lib/components/Popover';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import DefaultSearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import Filter from '@decisiv/ui-components/lib/components/Filter';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import ActionsPopover, { PopoverActions } from 'components/ActionsPopover';
import { OptionsList, createForm, FormHandler } from 'components/Form';
import EmptyResults from 'components/EmptyResults';

import useDebouncedValue from 'hooks/useDebouncedValue';
import HighlightText from 'components/HighlightedText';
import {
  BaseFilterProps,
  SimpleFilterProps,
} from '@decisiv/ui-components/lib/components/Filter/types';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { OptionsContainer } from './styles';

export interface SearchableFormValues {
  value: Item['id'][];
}

const { Form, FormProvider } = createForm<SearchableFormValues>();

const SEARCHABLE_FILTER_MODES = {
  SYNC: 'sync',
  ASYNC: 'async',
} as const;

type SearchableFilterModes = typeof SEARCHABLE_FILTER_MODES;
type SearchableFilterMode =
  SearchableFilterModes[keyof typeof SEARCHABLE_FILTER_MODES];

interface ContentProps
  extends Pick<PopoverProps, 'target' | 'zIndex' | 'placement'> {
  items?: Item[];
  onSubmit: (data: SearchableFormValues) => void;
  popoverLabel: string;
  width?: string;
  onClose?: () => void;
  mode?: SearchableFilterMode;
  searchValue?: string;
  debouncedSearchValue?: string;
  onSearch?: (searchText: string) => void;
  loading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

const Content = forwardRef<InstanceRef, ContentProps>((props, ref) => {
  const {
    popoverLabel,
    items = [],
    onSubmit,
    target,
    zIndex = 5,
    width = rem(spacing.base * 30),
    onClose,
    loading,
    loadingMore,
    mode,
    searchValue,
    debouncedSearchValue,
    onSearch,
    onLoadMore,
    placement,
  } = props;

  const [searchSyncValue, setSearchSyncValue, debouncedSyncSearchValue] =
    useDebouncedValue('');

  const {
    formState: { isValid, isDirty },
  } = useFormContext();

  const searchString =
    mode === SEARCHABLE_FILTER_MODES.SYNC ? searchSyncValue : searchValue;
  const debouncedSearchString =
    mode === SEARCHABLE_FILTER_MODES.SYNC
      ? debouncedSyncSearchValue
      : debouncedSearchValue;

  const formRef = useRef<FormHandler<SearchableFormValues>>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useInfiniteScroll({
    ref: scrollContainerRef,
    onBottomReached: onLoadMore,
    enabled: !loadingMore && !loading && mode === SEARCHABLE_FILTER_MODES.ASYNC,
    threshold: 50,
  });

  const filteredItems = useMemo(() => {
    if (mode === SEARCHABLE_FILTER_MODES.SYNC) {
      return items.filter((item) =>
        item.label
          .toLowerCase()
          .includes(debouncedSyncSearchValue.toLowerCase()),
      );
    }

    return items;
  }, [mode, items, debouncedSyncSearchValue]);

  const handleOnSubmit = useCallback(async () => {
    await formRef.current?.submit();
    onClose?.();
  }, [onClose]);

  const handleOnClear = useCallback(() => {
    formRef.current?.setValue('value', []);
    handleOnSubmit();
  }, [handleOnSubmit]);

  const handleOnInputChange = useCallback(
    (event) => {
      if (mode === SEARCHABLE_FILTER_MODES.SYNC) {
        return setSearchSyncValue(event.target.value);
      }

      return onSearch?.(event.target.value);
    },
    [mode, onSearch, setSearchSyncValue],
  );

  const actions = useMemo(
    () =>
      ({
        left: [
          {
            id: 'apply',
            text: t`Save`,
            disabled: !isValid || !isDirty,
            onClick: handleOnSubmit,
          },
          {
            id: 'cancel',
            text: t`Cancel`,
            kind: 'secondary',
            onClick: onClose,
          },
        ],
        right: [
          {
            id: 'clear-all',
            text: t`Clear All`,
            variant: 'ghost',
            onClick: handleOnClear,
          },
        ],
      } satisfies PopoverActions),
    [isDirty, isValid, handleOnSubmit, handleOnClear, onClose],
  );

  return (
    <ActionsPopover
      ref={ref}
      target={target}
      actions={actions}
      zIndex={zIndex}
      width={width}
      visible
      onRequestHide={onClose}
      placement={placement}
    >
      <Form ref={formRef} onSubmit={onSubmit} flexDirection="column">
        <Flex padding={1} flexDirection="column" flex={1}>
          <P marginBottom={0.5}>{popoverLabel}</P>
          <DefaultSearchInput
            size="small"
            searchOn="change"
            value={searchString || ''}
            onChange={handleOnInputChange}
          />
        </Flex>
        <OptionsContainer ref={scrollContainerRef}>
          {loading && (
            <Flex padding={2} justifyContent="center">
              <Loading title={t`Loading...`} />
            </Flex>
          )}
          {!loading && isEmpty(filteredItems) && (
            <EmptyResults
              icon={Search}
              maxWidth={rem(spacing.base * 25)}
              padding={2}
              size="small"
              iconSize="medium"
              color="licoriceMousse"
              title={t`No Matches Found`}
              subtitle={t`Try removing or editing some of your criteria or search for different words`}
            />
          )}
          {!loading && !isEmpty(filteredItems) && (
            <OptionsList
              multiple
              name="value"
              items={filteredItems}
              renderOptionLabel={(option) => {
                const { id, label } = option;

                return (
                  <Flex flexDirection="column" key={id}>
                    <HighlightText
                      text={label}
                      searchText={debouncedSearchString}
                    />
                  </Flex>
                );
              }}
            />
          )}
          {loadingMore && !loading && (
            <Flex
              data-testid="loading-more"
              justifyContent="center"
              padding={1}
            >
              <Loading size="small" title={t`Loading more...`} />
            </Flex>
          )}
        </OptionsContainer>
      </Form>
    </ActionsPopover>
  );
});

interface SearchableFilterProps
  extends Omit<ContentProps, 'target'>,
    Pick<SimpleFilterProps, 'text' | 'badgeIcon' | 'badgeColor' | 'endIcon'> {
  value?: Item['id'][];
  kind?: BaseFilterProps['kind'];
  validationSchema?: any;
  ExpandedIcon?: SimpleFilterProps['endIcon'];
  CollapsedIcon?: SimpleFilterProps['endIcon'];
}

const SearchableFilter = (props: SearchableFilterProps) => {
  const {
    badgeIcon: _badgeIcon,
    badgeColor: _badgeColor,
    endIcon: _endIcon,
    ExpandedIcon = ChevronUp,
    CollapsedIcon = ChevronDown,
    value = [],
    validationSchema,
    text,
    items,
    popoverLabel,
    onSubmit,
    mode = SEARCHABLE_FILTER_MODES.SYNC,
    onSearch,
    kind = 'primary',
    ...rest
  } = props;

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const triggerRef = useRef(null);

  const endIcon = isPopoverVisible ? ExpandedIcon : CollapsedIcon;

  const onClose = useCallback(() => {
    setIsPopoverVisible(false);
  }, []);

  const handleOnClose = useCallback(() => {
    if (mode === SEARCHABLE_FILTER_MODES.SYNC) {
      onClose?.();
      return;
    }

    onSearch?.('');
    onClose?.();
  }, [mode, onClose, onSearch]);

  return (
    <>
      <Filter
        ref={triggerRef}
        text={text}
        kind={kind}
        type="button"
        variant="action"
        onClick={() => setIsPopoverVisible((oldState) => !oldState)}
        endIcon={endIcon}
        active={value.length > 0}
      />
      {isPopoverVisible && (
        <FormProvider
          initialValues={{ value }}
          validationSchema={validationSchema}
        >
          <Content
            items={items}
            popoverLabel={popoverLabel}
            onSubmit={onSubmit}
            target={triggerRef}
            onClose={handleOnClose}
            onSearch={onSearch}
            mode={mode}
            {...rest}
          />
        </FormProvider>
      )}
    </>
  );
};

export default SearchableFilter;
